import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ClickService} from '../core/services/click.service';
import {NativeService} from '../core/services/native.service';
import {AlarmsService} from '../core/services/alarms.service';
import {TileHistoryService} from '../core/services/tile-history.service';
import {SessionService} from '../core/services/session.service';
import {AsideService, AsidePosition} from '../core/services/aside.service';
import {Store} from '@ngrx/store';
import {AppState} from './app.state';
import {Router, RouterOutlet, NavigationEnd} from '@angular/router';
import {EnvService, CURRENT_PLATFORM} from '../core/services/env.service';
import {Platform} from '../core/models/platform.enum';
import {SearchService} from '../core/services/search.service';
import {ObjectModel} from '../shared/models/object.model';
import {UpdateNotificationService} from '../core/services/update-notification.service';
import {UrlService} from '../core/services/url.service';
import {Filters} from '../shared/models/filter';
import {NgClass} from '@angular/common';
import {PageHeaderComponent} from '../lib/page-elements/page-header/page-header.component';
import {PageAsideComponent} from '../lib/page-elements/page-aside/page-aside.component';
import {TileHistoryDrawerComponent} from '../lib/components/tile-history-drawer/tile-history-drawer.component';
import {HelpBubbleComponent} from '../lib/components/help-bubble/help-bubble.component';
import {ShareManagerComponent} from '../lib/components/share-manager/share-manager.component';
import {TileHistoryViewportBufferComponent} from '../lib/components/tile-history-viewport-buffer/tile-history-viewport-buffer.component';
import {ShadeToolsComponent} from '../lib/page-elements/shade-tools/shade-tools.component';
import {BulkModalComponent} from '../features/tile-bulk-operations/shared/components/bulk-modal/bulk-modal.component';
import { ShareService } from 'src/moti/services/share.service';
import { filter } from 'rxjs/operators';
import { CopyToastComponent } from '../shared/components/copy-toast/copy-toast.component';
import { CopyToastService } from '../shared/components/copy-toast/copy-toast.service';

@Component({
  selector: 'motn-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    PageHeaderComponent,
    RouterOutlet,
    PageAsideComponent,
    TileHistoryDrawerComponent,
    HelpBubbleComponent,
    ShareManagerComponent,
    TileHistoryViewportBufferComponent,
    ShadeToolsComponent,
    BulkModalComponent,
    CopyToastComponent,
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  asidePosition: AsidePosition = 'fixed';
  showTileHistory: boolean = false;
  Platform = Platform;
  

  searchTerm: string = '';
  searchResultQty: number = 0;

  filters: Filters;

  touchedAside: TouchEvent;
  swipedAsideOpen: boolean = false;
  touchingAside: boolean = false;


  showShareManagerForObject: ObjectModel;

  platform: Platform;
  @ViewChild('iosBackButton') iosBackButtonEl: ElementRef<HTMLElement>;
  private iosReloadTimeout: ReturnType<typeof setTimeout>;
  iosBackButtonEnabled: boolean = false;

  private _asideEl: HTMLElement;


  constructor(
    // DO NOT REMOVE these services ⤵
    private envService: EnvService, // this should come first, as other depend on it
    private nativeService: NativeService,
    private alarmService: AlarmsService,
    private clickService: ClickService,
    // DO NOT REMOVE these services ⤴
    private router: Router,
    private store: Store<AppState>,
    private el: ElementRef,
    private tileHistoryService: TileHistoryService,
    private sessionService: SessionService,
    private asideService: AsideService,
    private searchService: SearchService,
    private urlService: UrlService,
    private shareService: ShareService,
    private updateNotificationService: UpdateNotificationService,
    public copyToastService: CopyToastService,
  ) {
    setTimeout(() => {
      this.iosBackButtonEnabled = true;
    }, 1000);
  }


  get asideEl(): HTMLElement {
    if (this._asideEl) {
      return this._asideEl;
    }

    const query = this.el.nativeElement.getElementsByTagName('aside');

    if (query.length > 0) {
      this._asideEl = query[0];
    }

    return this._asideEl;
  }


  ngOnInit() {
    this.platform = CURRENT_PLATFORM;

    /**
     * Do not remove this. It serves the entire app, anywhere a component is listening
     * for a document-level click.
     *
     * @param $event
     */
    this.el.nativeElement.addEventListener('mousedown', ($event) => {
      this.clickService.click$.next($event.target);
    });

    this.setUpAsideTouchListeners();

    this.asideService.asidePosition$.subscribe(
      (state) => {
        if (state) {
          this.asidePosition = state;
        }
      },
    );

    this.tileHistoryService.drawerActive$.subscribe(
      (data) => {
        this.showTileHistory = data;
      },
    );

    this.clickService.click$.subscribe(
      (el) => {
        const asideEl = document.getElementsByTagName('aside')[0];

        if (this.swipedAsideOpen) {
          if (!asideEl.contains(el)) {
            this.swipedAsideOpen = false;

          }
        }
      },
    );

    this.shareService.showShareManagerForObject$.subscribe(
      (object) => {
        this.showShareManagerForObject = object;
      },
    );

    if (window.innerWidth < 400 || window.innerHeight < 400) {
      // document.body.style.setProperty('zoom', '88%');
    }

    this.subscribeToSearchAndFilters();
  }


  ngAfterViewInit() {
    setTimeout(() => {
      this.nativeService.hideSplash();
      this.checkForAppUpdates();
    }, 500);
  }

  /**
   * Check for app updates using Firebase Remote Config
   */
  private async checkForAppUpdates(): Promise<void> {
    try {
      await this.updateNotificationService.checkForAppUpdates();
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  }

  ngOnDestroy() {
    // Cleanup any subscriptions if needed
  }


  navigateBack() {
    window.history.back();
  }


  onTouchBackButton() {
    this.iosBackButtonEl.nativeElement.classList.add('touched');

    this.iosReloadTimeout = setTimeout(() => {
      window.location.reload();
    }, 2000);
  }


  onUntouchBackButton() {
    this.iosBackButtonEl.nativeElement.classList.remove('touched');

    clearTimeout(this.iosReloadTimeout);
  }


  private subscribeToSearchAndFilters() {
    this.urlService.filters$.subscribe({
      next: (filters) => {
        this.filters = filters;
      },
    });

    this.urlService.searchTerm$.subscribe({
      next: (term) => {
        this.searchTerm = term;
      },
    });

    this.searchService.searchResultQuantity$.subscribe({
      next: (qty) => {
        // Deferred to avoid NG0100 when a child's ngOnInit sets this synchronously.
        queueMicrotask(() => {
          this.searchResultQty = qty;
        });
      },
    });
  }


  get isLoggedIn(): boolean {
    return this.sessionService.hasAuthenticated();
  }


  /**
   * This was necessary because, unfortunately, the touch events' handling in
   * Angular templating was resulting in the aside element breaking if anyone
   * attempted to swipe the aside element while the page was scrolling.
   *
   * @private
   */
  private setUpAsideTouchListeners() {
    this.el.nativeElement.addEventListener('touchstart', ($event) => {
      if ($event.target === this.asideEl) {
        this.touchAsideStart($event);
      }
    });

    this.el.nativeElement.addEventListener('touchmove', ($event) => {
      if ($event.target === this.asideEl) {
        this.touchAsideMove($event);
      }
    });

    this.el.nativeElement.addEventListener('touchend', ($event) => {
      if ($event.target === this.asideEl) {
        this.touchAsideEnd($event);
      }
    });
  }


  touchAsideStart($event: TouchEvent) {
    if (!this.swipedAsideOpen) {
      // preventDefault prevents mobile browsers' "swipe to go back"
      // gesture, thus making a swipe from the left side of the screen
      // open the drawer, if it's closed.
      //
      // Limit only to gestures in the first 25 pixels vertically so
      // preventDefault doesn't stop other things from working.
      if ($event.changedTouches[0].clientX < 25) {
        $event.preventDefault();
      }

      this.touchingAside = true;
    }

    this.touchedAside = $event;
  }


  touchAsideMove($event: TouchEvent) {
    // Prevents page from scrolling during touch move
    $event.preventDefault();

    this.touchingAside = false;

    const start: TouchEvent = this.touchedAside;
    const move: TouchEvent = $event;

    if (this.swipedAsideOpen) {
      if (start.changedTouches[0].screenX > move.changedTouches[0].screenX + 30) {
        this.swipedAsideOpen = false;
      }
    } else {
      if (start.changedTouches[0].screenX + 30 < move.changedTouches[0].screenX) {
        this.swipedAsideOpen = true;
      }
    }
  }


  touchAsideEnd($event: TouchEvent) {
    this.touchingAside = false;

    const start: TouchEvent = this.touchedAside;
    const end: TouchEvent = $event;

    if (this.swipedAsideOpen) {
      if (start.changedTouches[0].screenX > end.changedTouches[0].screenX + 30) {
        this.swipedAsideOpen = false;
      }
    } else {
      if (start.changedTouches[0].screenX + 30 < end.changedTouches[0].screenX) {
        this.swipedAsideOpen = true;
      }
    }
  }


  onCloseShareManager() {
    this.shareService.showShareManagerForObject$.next(null);
  }
}
